// Pure helpers for the Screenplay module. No React, no API.

export const uid = () => Math.random().toString(36).slice(2, 14);

export const SCENE_PRESETS = {
  es: [
    "INT. — DÍA",
    "INT. — NOCHE",
    "EXT. — DÍA",
    "EXT. — NOCHE",
    "INT./EXT. — DÍA",
  ],
  en: [
    "INT. — DAY",
    "INT. — NIGHT",
    "EXT. — DAY",
    "EXT. — NIGHT",
    "INT./EXT. — DAY",
  ],
};

export const CHAR_FIELDS = [
  { id: "age", label: "Edad", placeholder: "35 / Joven / Indeterminada" },
  { id: "physical", label: "Físico", placeholder: "Descripción física relevante" },
  { id: "personality", label: "Personalidad", placeholder: "Rasgos principales" },
  { id: "background", label: "Historia", placeholder: "Contexto biográfico" },
  { id: "motivation", label: "Motivación", placeholder: "Qué quiere" },
  { id: "conflict", label: "Conflicto", placeholder: "Conflicto interno o externo" },
  { id: "voice", label: "Voz", placeholder: "Cómo habla, muletillas, registro" },
  { id: "relationships", label: "Relaciones", placeholder: "Con otros personajes" },
  { id: "notes", label: "Notas", placeholder: "Notas estructuradas" },
  { id: "free", label: "Espacio libre", placeholder: "Lo que sea" },
];

// Categorías dramatúrgicas para ordenar/agrupar la matriz de personajes.
// `order` define el orden de aparición en el panel (1 = arriba del todo).
export const CHARACTER_CATEGORIES = [
  { id: "protagonista", label: "Protagonistas", short: "Protagonista", order: 1 },
  { id: "secundario", label: "Secundarios", short: "Secundario", order: 2 },
  { id: "esporadico", label: "Secundarios esporádicos", short: "Esporádico", order: 3 },
  { id: "figuracion", label: "Figuración con frase", short: "Figuración", order: 4 },
];
export const CHARACTER_CATEGORY_IDS = CHARACTER_CATEGORIES.map((c) => c.id);

// Compute scene numbers from elements (matches backend index_scenes)
export function computeSceneNumbers(elements) {
  const map = {};
  let n = 0;
  (elements || []).forEach((el) => {
    if (el.type === "scene_heading") {
      n += 1;
      map[el.id] = n;
    }
  });
  return map;
}

// Compute scenes index for the sidebar minimap
export function computeScenesIndex(elements) {
  const scenes = [];
  let current = null;
  (elements || []).forEach((el, idx) => {
    if (el.type === "scene_heading") {
      if (current) scenes.push(current);
      current = {
        elementId: el.id,
        number: scenes.length + 1,
        heading: el.text || "Nueva escena",
        index: idx,
        characters: new Set(),
        firstAction: "",
      };
    } else if (current) {
      if (el.type === "action" && !current.firstAction) {
        current.firstAction = (el.text || "").slice(0, 80);
      }
      if (el.type === "character") {
        const base = (el.text || "").split("(")[0].trim();
        if (base) current.characters.add(base);
      }
    }
  });
  if (current) scenes.push(current);
  return scenes.map((s) => ({ ...s, characters: Array.from(s.characters) }));
}

// Detect the "writing context" at the cursor: current scene heading, characters present,
// last action. Returns null if no anchor can be derived.
export function computeCursorContext(elements, cursorId, fallbackId) {
  if (!elements?.length) return null;
  const anchorId = cursorId || fallbackId || elements[elements.length - 1].id;
  const anchorIdx = elements.findIndex((e) => e.id === anchorId);
  if (anchorIdx < 0) return null;
  let headingIdx = -1;
  for (let i = anchorIdx; i >= 0; i--) {
    if (elements[i].type === "scene_heading") {
      headingIdx = i;
      break;
    }
  }
  const heading = headingIdx >= 0 ? elements[headingIdx].text || "(escena sin título)" : null;
  const charsSet = new Set();
  let lastAction = "";
  const startIdx = headingIdx >= 0 ? headingIdx : 0;
  for (let i = startIdx; i <= anchorIdx; i++) {
    const el = elements[i];
    if (el.type === "character") {
      const base = (el.text || "").split("(")[0].trim();
      if (base) charsSet.add(base);
    } else if (el.type === "action" && (el.text || "").trim()) {
      lastAction = el.text;
    }
  }
  return {
    anchorId,
    heading,
    sceneNumber: headingIdx >= 0
      ? Object.values(computeSceneNumbers(elements.slice(0, headingIdx + 1))).pop()
      : null,
    characters: Array.from(charsSet),
    lastAction: lastAction.slice(0, 240),
    isEndOfScript: anchorIdx === elements.length - 1,
  };
}

// Build the enriched instruction prefix sent to AI when cursor context is used.
export function buildContextPrefix(cursorContext) {
  if (!cursorContext) return "";
  const lines = [];
  if (cursorContext.heading) {
    lines.push(
      `Contexto: vengo de la escena ${cursorContext.sceneNumber ? "#" + cursorContext.sceneNumber + " " : ""}"${cursorContext.heading}".`
    );
  }
  if (cursorContext.characters?.length) {
    lines.push(`Personajes presentes: ${cursorContext.characters.join(", ")}.`);
  }
  if (cursorContext.lastAction) {
    lines.push(`Última acción escrita: "${cursorContext.lastAction}".`);
  }
  return lines.join(" ");
}

export function countWords(elements) {
  return (elements || []).reduce(
    (acc, el) => acc + (el.text || "").trim().split(/\s+/).filter(Boolean).length,
    0
  );
}

// Recopila TODOS los nombres de personaje conocidos para autocompletado:
//   - los del panel de Personajes (con ficha)
//   - los que aparecen en líneas de tipo "character" del propio guion
//     (capta nombres tipeados sin crear ficha)
// Devuelve un array único, en mayúsculas y ordenado alfabéticamente.
// Limpia paréntesis "(O.S.)", "(V.O.)" del nombre porque queremos sugerir
// solo el identificador del personaje.
export function collectCharacterSuggestions(elements, characters) {
  const set = new Set();
  (characters || []).forEach((c) => {
    const n = (c?.name || "").trim();
    if (n) set.add(n.toUpperCase());
  });
  (elements || []).forEach((el) => {
    if (el.type !== "character") return;
    const base = (el.text || "").split("(")[0].trim();
    if (base) set.add(base.toUpperCase());
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
}

